import React, {useEffect, useRef, useState} from 'react';
import useAppDispatch from 'hooks/useAppDispatch';
import {useNavigate} from 'react-router-dom';
import {logout} from 'features/auth/authSlice';
import {validateEmail, validatePassword} from 'utils/validators';
import TextInput from 'components/inputs/TextInput';
import PasswordInput from 'components/inputs/PasswordInput';
import SubmitButton from 'components/SubmitButton';
import {AuthThunks} from 'features/auth/authThunks';

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    setIsFormValid(
      isEmailValid && isPasswordValid && email !== '' && password !== '',
    );
  }, [email, password]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (validateEmail(value)) {
      setEmailError(undefined);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);

    if (validatePassword(value)) {
      setPasswordError(false);
    }
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const resultAction = await dispatch(
      AuthThunks.login({email: email.trim(), password, rememberMe}),
    );
    if (AuthThunks.login.fulfilled.match(resultAction)) {
      navigate('/second-step-verification');
    }
  };

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  return (
    <form
      className="mt-12 needs-validation self-stretch w-full"
      onSubmit={handleSubmit}
      noValidate>
      <div className="flex flex-col space-y-6">
        <TextInput
          id="email"
          type="text"
          label="Email"
          value={email}
          onChange={handleEmailChange}
          error={emailError}
          placeholder="you@email.com"
          tabIndex={1}
          ref={emailInputRef}
        />
        <div>
          <PasswordInput
            label={'Password'}
            password={password}
            onPasswordChange={handlePasswordChange}
            passwordError={passwordError}
            showPassword={showPassword}
            onToggleShowPassword={() => setShowPassword(!showPassword)}
            inputId={'password'}
            data-testid={'password-field'}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <div className="flex items-center mt-4 mb-4">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={handleRememberMeChange}
              className="h-4 w-4 text-cyan-400 border-gray-300 rounded focus:text-cyan-400"
            />
            <label
              htmlFor="remember-me"
              className="ml-2 block text-sm font-light text-zinc-400">
              Remember me
            </label>
          </div>
          <div>
            <SubmitButton
              onClick={handleSubmit}
              label="Sign in"
              disabled={!isFormValid}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default Login;
